import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Circle, Link2, UserX } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Card, EmptyState } from "@/components/ranger/PageHeader";
import { StatusBadge } from "@/components/ranger/StatusBadge";
import { ConfirmButton } from "@/components/ranger/ConfirmButton";
import { OnlinePlayers } from "@/components/ranger/OnlinePlayers";
import { LinkAnnounce } from "@/components/ranger/LinkAnnounce";
import { PlayerRecords } from "@/components/ranger/PlayerRecords";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";

const LINK_STATE = { verified: "connected", pending: "connecting", expired: "unconfigured", revoked: "error" };

const Prereqs = ({ cid }) => {
  const [p, setP] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/discord/status"), api.get(`/communities/${cid}`)]).then(([b, c]) => setP({
      bot: b.data.state === "ready", guild: !!c.data.discord.guild_id, rcon: c.data.rcon_status.state === "connected",
      sync: b.data.command_sync_errors?.[c.data.discord.guild_id],
    }));
  }, [cid]);
  if (!p) return null;
  const row = (done, text) => (
    <li key={text} className="flex items-center gap-3 text-sm">
      {done ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4 text-slate-600" />}
      <span className={done ? "text-slate-300" : "text-slate-500"}>{text}</span>
    </li>
  );
  return (
    <div data-testid="linking-prereqs">
      <ol className="mb-5 list-decimal space-y-1.5 pl-5 text-sm text-slate-400">
        <li>Player runs <code className="text-[#E5202E]">/link</code> with their gamertag and console in the community Discord.</li>
        <li>They're linked instantly. Each gamertag can only belong to one Discord account; staff can unlink a wrong claim.</li>
      </ol>
      <ul className="space-y-2">{[row(p.bot, "Discord bot online"), row(p.guild, "Discord guild linked"), row(p.rcon, "WebRCON connected (needed to read chat)")]}</ul>
      {p.sync && <p className="mt-3 text-xs text-red-400">{p.sync}</p>}
    </div>
  );
};

const LinksTable = ({ rows, canRevoke, onRevoke }) => (
  <div className="overflow-x-auto">
    <Table data-testid="links-table">
      <TableHeader><TableRow className="border-[#1E2D4A]">{["Gamertag", "Platform", "Discord", "Status", "Requested", "Verified / closed", "Last attempt", ""].map((h) => <TableHead key={h} className="rg-label whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {rows.map((l) => (
          <TableRow key={l.id} data-testid={`link-row-${l.id}`} className="border-[#1E2D4A]/60 text-xs">
            <TableCell className="font-display text-base font-semibold text-slate-100">{l.gamertag}</TableCell>
            <TableCell className="font-mono uppercase text-slate-400">{l.platform}</TableCell>
            <TableCell><div className="text-slate-300">{l.discord_username}</div><div className="font-mono text-[10px] text-slate-400">{l.discord_id}</div></TableCell>
            <TableCell><StatusBadge state={LINK_STATE[l.status]} label={l.status} /></TableCell>
            <TableCell className="whitespace-nowrap text-slate-500">{fmtTime(l.created_at)}</TableCell>
            <TableCell className="whitespace-nowrap text-slate-400">{fmtTime(l.revoked_at || l.verified_at || (l.status === "expired" ? l.code_expires_at : null))}</TableCell>
            <TableCell className="max-w-xs text-amber-400">{l.last_attempt?.reason || "—"}</TableCell>
            <TableCell>{canRevoke && ["verified", "pending"].includes(l.status) && (
              <ConfirmButton testId={`link-revoke-${l.id}`} className="text-slate-300 hover:text-red-400" title="Unlink player?" description={`${l.gamertag} will be unlinked from ${l.discord_username}.`} onConfirm={() => onRevoke(l.id)}><UserX className="h-4 w-4" /></ConfirmButton>
            )}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export default function Players() {
  const { active, can } = useAuth();
  const [tab, setTab] = useState("verified");
  const [data, setData] = useState({ items: [], counts: {} });
  const load = useCallback(() => api.get(`/communities/${active.id}/links?status=${tab}`).then((r) => setData(r.data)), [active.id, tab]);
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);
  const revokeLink = async (id) => {
    try { await api.delete(`/communities/${active.id}/links/${id}`); toast.success("Player unlinked"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const tabBtn = (id, label) => (
    <button key={id} data-testid={`links-tab-${id}`} onClick={() => setTab(id)} className={`rounded-sm px-3 py-1.5 text-xs font-semibold uppercase tracking-wider ${tab === id ? "bg-[#E5202E]/15 text-[#E5202E]" : "text-slate-400 hover:text-slate-200"}`}>
      {label} <span className="font-mono text-slate-500">{data.counts[id] ?? 0}</span>
    </button>
  );

  return (
    <div>
      <PageHeader eyebrow="Phase 2 · Account linking" title="Players" description="Discord accounts verified against in-game Xbox and PS5 names. Links are per community." />
      <OnlinePlayers cid={active.id} />
      <div className="grid gap-5 lg:grid-cols-[1fr_2.4fr]">
        <Card title="How linking works" icon={Link2}><Prereqs cid={active.id} /></Card>
        <Card right={<div className="flex flex-wrap gap-1">{[tabBtn("verified", "Linked"), tabBtn("pending", "Pending"), tabBtn("closed", "Expired / revoked")]}</div>}>
          {data.items.length ? <LinksTable rows={data.items} canRevoke={can("moderator")} onRevoke={revokeLink} /> : (
            <EmptyState icon={Link2} title={tab === "verified" ? "No linked players yet" : "Nothing here"} testId="links-empty">Links appear when players use /link in Discord and pick their gamertag and console.</EmptyState>
          )}
        </Card>
      </div>
      <PlayerRecords cid={active.id} />
      {can("moderator") && <div className="mt-5 max-w-2xl"><LinkAnnounce cid={active.id} canEdit={can("admin")} /></div>}
    </div>
  );
}
